import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Divider } from '@mui/material';
import { useCartStore } from '../../store/cartStore';
import { formatCurrency } from '../../utils/currency';

export default function CartCheckoutConfirmPage() {
  const { cartId } = useParams<{ cartId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const purchaser = (location.state as any)?.purchaser;

  const { cart, loadCartById, placeOpenCart } = useCartStore();

  useEffect(() => {
    if (cartId) void loadCartById(Number(cartId));
  }, [cartId, loadCartById]);

  if (!purchaser) {
    // no data -> go back to checkout
    navigate(cartId ? `/cart/${cartId}/checkout` : '/cart');
    return null;
  }

  const mandatoryFields = ['name','surname','address','city','zip','telephone','email'];

  const subtotal = cart?.items?.reduce((s, i) => s + (i.quantity * (i.price ?? 0)), 0) ?? 0;

  const onConfirm = async () => {
    try {
      await placeOpenCart(purchaser);
      navigate('/cart');
    } catch (e: any) {
      alert(e?.message ?? 'Failed to place order');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 980, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', color: 'text.secondary', fontSize: 13, mb: 1 }}>
          <Typography sx={{ cursor: 'pointer' }}>Cart</Typography>
          <Typography component="span">•</Typography>
          <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>Confirm Order</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>Confirm Order</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
              Review mandatory purchaser fields and ordered items before placing the order.
            </Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 10, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>Secure checkout</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: '#fff', fontWeight: 700 }}>1</Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'primary.main' }}>Purchaser Details</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: '#fff', fontWeight: 700 }}>2</Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'primary.main' }}>Confirm Order</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, opacity: 0.65 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', border: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>3</Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Complete</Typography>
          </Box>
        </Box>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 10px 24px rgba(10,26,60,0.05)', mb: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>Purchaser Details</Typography>
        <Box sx={{ display: 'grid', gap: 1 }}>
          {mandatoryFields.map((k) => (
            <Box key={k} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, gap: 1, py: 1, px: 2, borderRadius: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600, textTransform: 'capitalize' }}>{k}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.primary', whiteSpace: 'pre-wrap' }}>{String(purchaser[k] ?? '—')}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <Paper sx={{ p: 0, borderRadius: 3, overflow: 'hidden', mb: 3, boxShadow: '0 10px 24px rgba(10,26,60,0.05)' }}>
        <Box sx={{ px: 2, py: 2, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Items in order</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Part No.</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Line Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cart?.items?.map((line) => (
              <TableRow key={line.cartItemId}>
                <TableCell>{line.partNumber}</TableCell>
                <TableCell>{line.description}</TableCell>
                <TableCell align="right">{line.quantity}</TableCell>
                <TableCell align="right">{formatCurrency((line.price ?? 0) * line.quantity)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>Subtotal:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(subtotal)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
            Fields marked <Box component="span" sx={{ color: 'error.main' }}>*</Box> are required
          </Typography>
          <Button variant="contained" onClick={onConfirm}>Confirm & Place Order</Button>
        </Box>
      </Box>
    </Box>
  );
}
